// Pure data and CSS generator — no browser side effects, safe to import in SSR.
// `theme.ts` imports from here so the base theme is defined in one place.

import { type Theme } from "./types";

export const baseTheme: Theme = {
  config: {
    useSystemColorMode: false,
    initialColorMode: "light",
  },
  color: {
    white: "#FFFFFF",
    stone: {
      100: "#FAFAF9",
      200: "#F5F5F4",
      300: "#E7E5E4",
      400: "#D7D3D1",
      500: "#A6A09B",
      600: "#78716B",
      700: "#58534D",
      800: "#45403C",
      900: "#292524",
      1000: "#1B1917",
    },
  },
  spacing: {
    0: "0",
    1: "0.25rem",
    2: "0.5rem",
    3: "0.75rem",
    4: "1rem",
    5: "1.25rem",
    6: "1.5rem",
    8: "2rem",
    9: "2.25rem",
    10: "2.5rem",
    12: "3rem",
    16: "4rem",
    20: "5rem",
    24: "6rem",
    32: "8rem",
    40: "10rem",
    48: "12rem",
    56: "14rem",
    64: "16rem",
  },
  weight: {
    regular: 400,
    medium: 500,
  },
  zIndex: {
    hide: -1,
    auto: "auto",
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  },
  font: {
    regular: "FTRegularNeue, sans-serif",
    medium: "FTRegularNeue, sans-serif",
    display: "BigSans, sans-serif",
  },
  shadows: {
    sm: "0px 1px 4px 0px rgba(0, 0, 0, 0.04)",
    lg: "0px 4px 24px 0px rgba(0, 0, 0, 0.08)",
  },
  layout: {
    headerHeight: "83px",
    headerHeightMobile: "70px",
    headerHeightMobileHalf: "35px",
  },
};

export function generateBaseTokensCss(theme: Theme): string {
  let css = ":root{";

  Object.entries(theme.color).forEach(([colorName, scale]) => {
    if (typeof scale === "object") {
      Object.entries(scale).forEach(([shade, value]) => {
        css += `--color-${colorName}-${shade}:${value};`;
      });
    } else {
      css += `--color-${colorName}:${scale};`;
    }
  });

  Object.entries(theme.font).forEach(([fontName, value]) => {
    css += `--font-${fontName}:${value};`;
  });

  Object.entries(theme.shadows).forEach(([shadowName, value]) => {
    css += `--shadow-${shadowName}:${value};`;
  });

  Object.entries(theme.spacing).forEach(([spaceName, value]) => {
    css += `--spacing-${spaceName}:${value};`;
  });

  Object.entries(theme.weight).forEach(([weightName, value]) => {
    css += `--weight-${weightName}:${value};`;
  });

  Object.entries(theme.zIndex).forEach(([indexName, value]) => {
    css += `--z-index-${indexName}:${value};`;
  });

  Object.entries(theme.layout).forEach(([key, value]) => {
    css += `--layout-${key}:${value};`;
  });

  css += "}";
  css += `@media(max-width:768px){:root{--layout-headerHeight:${theme.layout.headerHeightMobile};}}`;

  return css;
}
